import { debounce, setRef } from "@mui/material";
import { RefCallback, RefObject, useCallback, useEffect, useRef, useState } from "react";

interface UseElementResizeAndViewportOptions {
  debounceMs?: number;
  callback: (isOverflowViewport: boolean, e: ResizeObserverEntry) => void;
  onResize?: (e: ResizeObserverEntry) => void;
  triggerOnce?: boolean;
}

export const useElementResizeAndViewportOverflow = <TElement extends HTMLElement>(
  options: UseElementResizeAndViewportOptions
): [RefObject<TElement | null>, RefCallback<TElement>] => {
  const ref = useRef<TElement>(null);
  const { debounceMs = 100, callback, triggerOnce = false, onResize } = options;
  const [elementRef, setElementRef] = useState<TElement | null>(null);
  const lastOverflowState = useRef<boolean>(false);

  useEffect(() => {
    const element = elementRef;
    if (!element) return;

    const observer = new ResizeObserver(([iOEntry]) => {
      const viewportHeight = globalThis.visualViewport?.height ?? document.documentElement.clientHeight;
      const isOverflowViewport = iOEntry.target.getBoundingClientRect().bottom > viewportHeight;

      debounce(() => {
        if (isOverflowViewport !== lastOverflowState.current) {
          lastOverflowState.current = isOverflowViewport;
          callback(isOverflowViewport, iOEntry);
          if (triggerOnce && isOverflowViewport) {
            observer.unobserve(element);
          }
        }
      }, debounceMs)();
      onResize?.(iOEntry);
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [triggerOnce, callback, debounceMs, elementRef, onResize]);

  const setElRef = useCallback((instance: TElement | null) => {
    setRef(ref, instance);
    setElementRef(instance);
  }, []);

  return [ref, setElRef];
};
